import React, { useState } from "react";
import { Helmet } from "react-helmet";
import { Col, Container, Row } from "reactstrap";
import { useHistory } from "react-router-dom";

import viewIcon from "../../assets/images/app/common/listing-view-image.svg";
import editIcon from "../../assets/images/app/allaya/edit-icon.svg";
import deleteIcon from "../../assets/images/app/allaya/delete-icon.svg";

import { Pagination } from "../../components/Pagination";
import { DataTable } from "../../components/DataTable";
import { DeleteNotification, GetNotification } from "../../api/api.service";
import { useLocalPaginatedRequest } from "../../hooks/useLocalPaginatedRequest";
import { NoticationStatus, PageTitles } from "../../constants";
import { MODULE_KEYS, getKey } from "../../api/api.keys";
import { SearchBox } from "../../components/SearchBox";

import { APP_ROUTES, getParamsAttachedRoute } from "../../helpers/routeHelpers";
import { SummaryCard } from "../../components/SummaryCard";
import { LABELS } from "./Config";
import { transformFiltersToArray, truncateString } from "../../utils/commonUtils";
import SubscriptionStatus, { SUBSCRIPTION_STATUS } from "../../constants/SubscriptionStatus";

import { ReactComponent as FilterIcon } from "../../assets/images/app/allaya/filter-autocomplete.svg";
import FilterModal from "./FilterModal";
import { FilterChips } from "./FilterChips";
import { useRequestProcessor } from "../../hooks/useRequestProcessors";
import { CustomButton } from "../../components/CustomButton";
import { dateTimeFormat } from "../../utils/dateFormatUtils";
import { formatTime12Hour, monthYear } from "../../utils/dateUtils";
import { NOTIFICATION_STATUS } from "../../constants/NoticationStatus";
import { ConfirmationModal } from "../../components/CustomModal";
import { showToast, SUCCESS } from "../../utils/toastUtils";

let { TITLE, SCHEDULE_DATE, SCHEDULE_TIME, AGE_GROUP, USER_GROUP, STATUS, ACTION } = LABELS;

const NotificationManagement = () => {
    const history = useHistory();
    const [isNotificationDelete, setIsNotificationDelete] = useState(false);
    const [notificationID, setNotificationID] = useState(null);
    const [isFilterModal, setIsFilterModal] = useState(false);
    const [filters, setFilters] = useState({
        subCategory: [],
        status: [],
        userType: []
    });

    const {
        isFetching,
        page,
        handlePageClick,
        total,
        data,
        searchText,
        limit,
        refetch,
        handleSearchChange,
        handleSortingChange
    } = useLocalPaginatedRequest({
        key: getKey([MODULE_KEYS.NOTIFICATION_MANAGEMENT_LISTING]),
        requestFn: GetNotification,
        dataKey: "data",
        totalKey: "total",
        params: {
            ...(filters.status?.value && {
                notificationStatus:
                    filters.status?.value === "Pending"
                        ? NOTIFICATION_STATUS.PENDING
                        : NOTIFICATION_STATUS.SENT
            }),
            ...(filters.userType?.value && {
                tierType:
                    filters.userType?.value === "All"
                        ? "ALL"
                        : filters.userType?.value === "Premium"
                        ? SUBSCRIPTION_STATUS.PREMIUM
                        : SUBSCRIPTION_STATUS.FREEMIUM
            })
        }
    });

    //  delete handling
    let { useMutate } = useRequestProcessor();

    let { mutate, isLoading } = useMutate("", getKey(), () => DeleteNotification(notificationID));

    // Open filter modal
    const openFilterModal = () => setIsFilterModal(true);

    // Handle applying filters
    const handleApplyFilter = (newFilters) => {
        setFilters(newFilters);
        setIsFilterModal(false);
    };

    const handleRemoveFilter = (type, value) => {
        setFilters((prevFilters) => {
            // Create a new object to avoid mutating the state
            const updatedFilters = { ...prevFilters };

            // Handle arrays (like `subCategory`)
            if (Array.isArray(updatedFilters[type])) {
                updatedFilters[type] = updatedFilters[type].filter((item) => item.value !== value);
            }

            // Handle objects (like `status` or `userType`)
            if (typeof updatedFilters[type] === "object" && updatedFilters[type] !== null) {
                if (updatedFilters[type].value === value) {
                    updatedFilters[type] = null; // Clear the object if the value matches
                }
            }

            return updatedFilters;
        });
    };

    const deleteClickHandler = (id) => {
        setNotificationID(id);
        setIsNotificationDelete(true);
    };

    const audioDeleteHandler = () => {
        mutate(
            {},
            {
                onSuccess: async (res) => {
                    setIsNotificationDelete(false);
                    showToast(SUCCESS, "Notification has been deleted successfully");
                    refetch();
                },
                onError: (err) => {
                    console.log("not cerated", err);
                    setIsNotificationDelete(false);
                }
            }
        );
    };

    return (
        <div className="page-content">
            <Helmet>
                <title>{PageTitles.USER_MANAGEMENT}</title>
            </Helmet>
            <Container fluid>
                <Row className="mb-3">
                    <Col sm="12" md="3" lg="3"></Col>
                    <Col sm="12" md="6" lg="6"></Col>
                    <Col sm="12" md="3" lg="3" align="right">
                        <CustomButton
                            title="Add Notification"
                            color="primary"
                            icon={<i className="bx bx-plus"></i>}
                            className="custom-btn-lg"
                            onClick={() => history.push(APP_ROUTES.NOTIFICATION_MANAGEMENT_ADD)}
                        />
                    </Col>
                </Row>
                <SummaryCard
                    cardProps={{
                        style: { padding: "0px" } // Custom border radius
                    }}
                >
                    <Row className="pt-3 px-3">
                        <Col sm="12" md="4" lg="4">
                            <SearchBox
                                className="me-3 w-50 "
                                value={searchText}
                                onChange={handleSearchChange}
                            />
                        </Col>
                        <Col sm="12" md="5" lg="5"></Col>

                        <Col sm="12" md="3" lg="3">
                            <div
                                className={`filter-container d-flex justify-content-between align-items-center pointer`}
                                onClick={openFilterModal}
                            >
                                <div className="position-relative filter-text">Filter By</div>
                                <div className="">
                                    <FilterIcon />
                                </div>
                            </div>
                        </Col>
                    </Row>

                    <Row className="px-3 my-2">
                        <FilterChips
                            filters={transformFiltersToArray(filters)}
                            onRemove={handleRemoveFilter}
                        />
                    </Row>

                    <DataTable
                        tableContainerClass=""
                        loading={isFetching}
                        data={data}
                        config={[
                            {
                                title: TITLE,
                                render: (data) => truncateString(data?.title, 30)
                            },

                            {
                                title: SCHEDULE_DATE,
                                render: (data) =>
                                    monthYear(dateTimeFormat.appDateFormat, data.scheduledAt) || "-"
                            },

                            {
                                title: SCHEDULE_TIME,
                                render: (data) => formatTime12Hour(data.scheduledAt) || "-"
                            },
                            {
                                title: AGE_GROUP,
                                render: (data) =>
                                    (
                                        <div className="d-flex flex-row gap-2">
                                            {data.appNotificationAgeBrackets.map((ageBracket) => (
                                                <div className="flex-row age-group">
                                                    {ageBracket?.ageBracket.endAge
                                                        ? `${ageBracket?.ageBracket.startAge} -
                                                        ${ageBracket?.ageBracket.endAge}`
                                                        : `${ageBracket?.ageBracket.startAge}+`}
                                                </div>
                                            ))}{" "}
                                        </div>
                                    ) || "-"
                            },
                            {
                                title: USER_GROUP,

                                render: (data) => (
                                    <div
                                        className={`status-common-class ${SubscriptionStatus.getLabelClass(
                                            data?.tierType
                                        )}`}
                                    >
                                        {SubscriptionStatus.getDisplayTextKey(data?.tierType) ||
                                            "-"}
                                    </div>
                                )
                            },
                            {
                                title: STATUS,

                                render: (data) => (
                                    <div
                                        className={`status-common-class ${NoticationStatus.getLabelClass(
                                            data?.notificationStatus
                                        )}`}
                                    >
                                        {NoticationStatus.getDisplayTextKey(
                                            data?.notificationStatus
                                        ) || "-"}
                                    </div>
                                )
                            },

                            {
                                title: ACTION,
                                render: (data) => {
                                    return (
                                        <div className="d-flex ">
                                            <img
                                                className="me-1"
                                                src={viewIcon}
                                                alt={"details icon"}
                                                role="button"
                                                onClick={() =>
                                                    history.push(
                                                        getParamsAttachedRoute(
                                                            APP_ROUTES.NOTIFICATION_MANAGEMENT_DETAILS,
                                                            {
                                                                notificationID: data?.id
                                                            }
                                                        )
                                                    )
                                                }
                                            />
                                            {data?.notificationStatus ===
                                                NOTIFICATION_STATUS.PENDING && (
                                                <img
                                                    className="me-2"
                                                    src={editIcon}
                                                    alt={"edit"}
                                                    role="button"
                                                    onClick={() =>
                                                        history.push(
                                                            getParamsAttachedRoute(
                                                                APP_ROUTES.NOTIFICATION_MANAGEMENT_UPDATE,
                                                                {
                                                                    notificationID: data?.id
                                                                }
                                                            )
                                                        )
                                                    }
                                                />
                                            )}

                                            <img
                                                className="me-1"
                                                src={deleteIcon}
                                                alt={"delete"}
                                                role="button"
                                                onClick={() => deleteClickHandler(data?.id)}
                                            />
                                        </div>
                                    );
                                }
                            }
                        ]}
                    />

                    <Pagination
                        rowLimit={limit}
                        onPageClick={handlePageClick}
                        currentPage={page}
                        totalData={total}
                        handleChange={(e) => handleSortingChange(e.target.value)}
                    />
                </SummaryCard>
            </Container>

            {isFilterModal && (
                <FilterModal
                    isModalOpen={isFilterModal}
                    handleClose={() => setIsFilterModal(false)}
                    handleApplyFilter={handleApplyFilter}
                    selectedFilters={filters} // Pass the current filters to the modal
                />
            )}

            <ConfirmationModal
                size="md"
                hasCustomConfig
                isOpen={isNotificationDelete}
                customImageSrc={deleteIcon}
                customSubText={"Are you sure you want to delete this notification?"}
                customYesBtnText={"Delete"}
                customNoBtnText={"Cancel"}
                onYes={audioDeleteHandler}
                onNo={() => {
                    setIsNotificationDelete(false);
                }}
                yesLoading={isLoading}
            />
        </div>
    );
};

export default NotificationManagement;
